import React, { useState } from "react";
import * as XLSX from "xlsx";
import api from "../../../../api/axios";
import { rowToArticle, applyHyperlinkTargets } from "./ArticleExcelIO";

const partTypeEndpoint = (partType) => {
    switch (partType) {
        case "SubGroup": return "/subgroup";
        case "Enclosure": return "/enclosure";
        case "Rail": return "/rail";
        case "Duct": return "/duct";
        default: return null;
    }
};

// Readable reason for a failed row (e.g. the backend's "Configurator URL must
// be an absolute http or https link.")
const errorText = (err) => {
    if (typeof err === "string") return err;
    const data = err?.response?.data;
    if (typeof data === "string" && data.trim()) return data.trim();
    if (data?.title || data?.message) return data.message ?? data.title;
    return err?.message ?? "Unknown error";
};

const articleLabel = (article, index) =>
    [article.manufacturerShortName, article.articleNumber].filter(Boolean).join(" ") ||
    article.name ||
    `Row ${index + 2}`; // +2: header row + 1-based

const ArticleExcelImporter = ({
    inputId = "article-excel-importer-trigger",
    ownership,       // "Tenant" | "Global"
    tenantId,
    defaultPartType = "SubGroup",
    onImportComplete,
}) => {
    const [status, setStatus] = useState("");

    const parseRows = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            const isCsv = file.name.toLowerCase().endsWith(".csv");

            reader.onload = (e) => {
                try {
                    const workbook = isCsv
                        ? XLSX.read(e.target.result, { type: "string" })
                        : XLSX.read(new Uint8Array(e.target.result), { type: "array" });

                    const sheet = workbook.Sheets[workbook.SheetNames[0]];
                    // Link cells showing a text ("Siemens configurator"): use the
                    // hyperlink address, not the visible text
                    applyHyperlinkTargets(sheet);
                    resolve(XLSX.utils.sheet_to_json(sheet, { defval: "" }));
                } catch (err) {
                    reject(err);
                }
            };

            reader.onerror = reject;

            if (isCsv) reader.readAsText(file);
            else reader.readAsArrayBuffer(file);
        });
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setStatus("Reading file...");

        try {
            const rows = await parseRows(file);

            const articles = rows.map((row) =>
                rowToArticle(row, { ownership, tenantId, defaultPartType })
            );

            let successCount = 0;
            let failCount = 0;
            const failedItems = [];

            for (const [index, article] of articles.entries()) {
                const endpoint = partTypeEndpoint(article.partType);

                if (!endpoint) {
                    failCount++;
                    failedItems.push({ article, index, error: "Unsupported Part Type" });
                    continue;
                }

                try {
                    if (article.id) {
                        await api.put(`${endpoint}/${article.id}`, article);
                    } else {
                        delete article.id;
                        await api.post(endpoint, article);
                    }
                    successCount++;
                } catch (err) {
                    failCount++;
                    failedItems.push({ article, index, error: err });
                    console.error("Failed to import article:", article, err);
                }
            }

            // Show why the first rows failed, so e.g. a bad link can be fixed in the file
            const details = failedItems
                .slice(0, 5)
                .map(({ article, index, error }) => `- ${articleLabel(article, index)}: ${errorText(error)}`)
                .join("\n");
            const more = failedItems.length > 5 ? `\n... and ${failedItems.length - 5} more (see console)` : "";

            alert(
                `Import finished.\nSuccessful: ${successCount}\nFailed: ${failCount}` +
                (details ? `\n\n${details}${more}` : "")
            );

            if (failedItems.length > 0) {
                console.warn("Failed items:", failedItems);
            }

            if (typeof onImportComplete === "function") await onImportComplete();
            setStatus(failCount ? "Import finished with errors." : "Import successful!");
        } catch (err) {
            console.error("Import failed:", err);
            setStatus("Import failed. Check console for details.");
        } finally {
            event.target.value = "";
        }
    };

    return (
        <div className="hidden-import">
            <input
                id={inputId}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: "none" }}
                onChange={handleFileUpload}
            />
            {status && <p className="import-status">{status}</p>}
        </div>
    );
};

export default ArticleExcelImporter;
